import { useEffect, useMemo, useState } from "react";
import "./BulkSelectPortsModal.css";

function BulkSelectPortsModal({
  open,
  ports,
  onClose,
  onSave,
  defaultSelected = [], 
  lockedLabels = [],   
}) {
  const allLabels = useMemo(() => {
    return Array.isArray(ports) ? ports.map((p) => String(p.label)) : [];
  }, [ports]);

  const defaultKey = useMemo(
    () =>
      Array.isArray(defaultSelected)
        ? defaultSelected.map(String).sort().join("|")
        : "",
    [defaultSelected]
  );
  const lockedKey = useMemo(
    () =>
      Array.isArray(lockedLabels)
        ? lockedLabels.map(String).sort().join("|")
        : "",
    [lockedLabels]
  );

  const lockedSet = useMemo(() => {
    const s = new Set();
    if (Array.isArray(lockedLabels)) lockedLabels.forEach((l) => s.add(String(l)));
    return s;
  }, [lockedKey]);

  const [selectedSet, setSelectedSet] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    const init = new Set();
    if (Array.isArray(defaultSelected)) {
      defaultSelected.forEach((l) => init.add(String(l)));
    }
    lockedSet.forEach((l) => init.add(l));
    setSelectedSet(init);
  }, [open, defaultKey, lockedKey]); 

  if (!open) return null;

  const isChecked = (label) => selectedSet.has(label) || lockedSet.has(label);

  const toggleSelect = (label) => {
    const lbl = String(label);
    if (lockedSet.has(lbl)) return; 
    setSelectedSet((prev) => {
      const next = new Set(prev);
      if (next.has(lbl)) next.delete(lbl);
      else next.add(lbl);
      return next;
    });
  };

  const toggleAll = () => {
    const everythingSelected = allLabels.every((l) => isChecked(l));
    if (everythingSelected) {
      setSelectedSet(new Set(lockedSet));
    } else {
      const all = new Set(allLabels);
      lockedSet.forEach((l) => all.add(l));
      setSelectedSet(all);
    }
  };

  const allChecked = allLabels.length > 0 && allLabels.every((l) => isChecked(l));

  return (
    <div className="bulk-root">
      <div className="bulk-backdrop" onClick={onClose} />

      <div className="bulk-card">
        <h3 className="bulk-title">Bulk Select Call Features - Port Selection</h3>
        <p className="bulk-subtitle">
          Select all ports to which the selected Call Features will be applied
        </p>

        <div className="bulk-grid">
          <label className="bulk-checkbox">
            <input type="checkbox" checked={allChecked} onChange={toggleAll} />
            <span className="bulk-label">Select All</span>
          </label>

          {allLabels.map((label) => {
            const locked = lockedSet.has(label);
            return (
              <label key={label} className="bulk-checkbox">
                <input
                  type="checkbox"
                  checked={isChecked(label)}
                  disabled={locked}
                  onChange={() => toggleSelect(label)}
                />
                <span className="bulk-label">
                  {label} {locked ? "(locked)" : ""}
                </span>
              </label>
            );
          })}
        </div>

        <div className="bulk-footer">
          <button className="cf-secondary" onClick={onClose}>
            Close
          </button>
          <button
            className="cf-primary"
            onClick={() => {
              const finalSet = new Set(selectedSet);
              lockedSet.forEach((l) => finalSet.add(l));
              onSave(Array.from(finalSet));
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default BulkSelectPortsModal;
